import { z } from 'zod';
import { identificarDocumento, normalizarDocumento } from '../documento/documento.js';
import { senhaSchema } from './equipe.js';
/**
 * Contratos de identidade do CLIENTE (chat web e app).
 *
 * Dois níveis (decisão D6):
 * - visitante: informa o documento e é atendido — não vê histórico nem dados sensíveis (Etapa 3);
 * - verificado: criou conta (documento → código no contato do ERP → senha) e vê todo o histórico.
 *
 * O código enviado por e-mail/SMS é chamado de OTP; o contato NUNCA é digitado pelo cliente,
 * vem sempre do cadastro no HubSoft.
 */
export const documentoSchema = z
    .string()
    .transform(normalizarDocumento)
    .refine((d) => identificarDocumento(d) !== null, 'Informe um CPF ou CNPJ válido');
/**
 * Um e-mail, grosso modo. Serve para separar do documento, não para validar endereço.
 *
 * As barras invertidas de `\s` e `\.` são o ponto inteiro desta linha. Sem elas — como
 * ficou até 28/09 — `[^s@]` vira "qualquer coisa menos a letra s", e então nenhum endereço
 * com "s" antes do arroba é reconhecido como e-mail. Na prática o técnico de liberação não
 * conseguia entrar: "teste@…" era recusado com "Informe um CPF ou CNPJ válido", porque
 * quem tem s no nome deixou de parecer e-mail.
 */
const PARECE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function pareceEmail(valor) {
    return PARECE_EMAIL.test(valor.trim());
}
/**
 * O que a pessoa digita no campo de entrada do chat.
 *
 * O cliente digita CPF ou CNPJ. O técnico de liberação digita o e-mail dele, no mesmo
 * campo e sem nada na tela anunciando isso — foi decisão de 26/09: o cliente não precisa
 * saber que existe outro tipo de acesso, e os técnicos aprendem no treinamento interno.
 * Por isso a mensagem de erro fala só de CPF/CNPJ.
 */
export const identificacaoSchema = z
    .string()
    .trim()
    .min(3)
    .max(150)
    // Normaliza antes de validar: o documento chega com pontos e traços, e quem consulta
    // depois procura pelo hash dos dígitos. Sem este passo a busca nunca acha ninguém.
    .transform((v) => (pareceEmail(v) ? v.toLowerCase() : normalizarDocumento(v)))
    .refine((v) => pareceEmail(v) || identificarDocumento(v) !== null, 'Informe um CPF ou CNPJ válido');
export const identificarSchema = z.object({ documento: identificacaoSchema });
export const CanalContato = { SMS: 'sms', EMAIL: 'email' };
export const enviarCodigoSchema = z.object({
    tokenIdentificacao: z.string().min(1),
    contatoId: z.string().min(1),
});
export const codigoOtpSchema = z
    .string()
    .transform((c) => c.replace(/\D/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'O código tem 6 dígitos'));
export const validarCodigoSchema = z.object({
    tokenIdentificacao: z.string().min(1),
    codigo: codigoOtpSchema,
});
export const criarContaSchema = z.object({
    tokenVerificado: z.string().min(1),
    senha: senhaSchema,
    /** Aceite do aviso de privacidade (LGPD): registramos versão e data. */
    aceitePrivacidade: z.literal(true, { error: 'É necessário aceitar o aviso de privacidade' }),
});
export const loginClienteSchema = z.object({
    /** CPF/CNPJ do cliente ou e-mail do técnico — o mesmo campo para os dois. */
    documento: identificacaoSchema,
    senha: z.string().min(1, 'Informe a senha'),
});
export const entrarComCodigoSchema = z.object({ tokenVerificado: z.string().min(1) });
/**
 * Trocar a senha estando dentro.
 *
 * Existe por causa do técnico de liberação: a senha dele é entregue pelo admin e nasce
 * temporária, e o caminho normal de "esqueci minha senha" depende de um código enviado ao
 * contato do ERP — que ele não tem.
 */
export const trocarSenhaClienteSchema = z.object({
    senhaAtual: z.string().min(1, 'Informe a senha atual'),
    novaSenha: senhaSchema,
});
export const redefinirSenhaSchema = z.object({
    tokenVerificado: z.string().min(1),
    novaSenha: senhaSchema,
});
export const TipoSolicitacaoTitular = {
    ACESSO: 'acesso',
    EXCLUSAO: 'exclusao',
    PORTABILIDADE: 'portabilidade',
};
export const solicitarExclusaoSchema = z.object({
    motivo: z.string().max(500).optional(),
});
//# sourceMappingURL=cliente.js.map